import {isPWA} from "./detectors";

export type InAppName =
  | "messenger"
  | "instagram"
  | "facebook"
  | "twitter"
  | "line"
  | "wechat"
  | "threads"
  | "tiktok"
  | "snapchat"
  | "linkedin"
  | "gsa"
  | "whatsapp"
  | "telegram"
  | "webview";

export type AppEnvironment = "pwa" | `inapp:${InAppName}` | "browser";

// Order matters: messenger is checked before facebook because the messenger
// regex also matches shared FBAN/FB_IAB patterns; named apps are checked
// before the generic WebView fallback. Ported from inapp-spy:
// https://github.com/shalanah/inapp-spy/blob/main/src/regexAppName.ts
const APP_REGEXES: ReadonlyArray<{name: InAppName; regex: RegExp}> = [
  {
    name: "messenger",
    regex:
      /(\bFB[\w_]+\/(Messenger))|(^(?!.*\buseragents)(?!.*\bIABMV).*(FB_IAB|FBAN).*)/i,
  },
  {name: "instagram", regex: /\bInstagram/i},
  {name: "facebook", regex: /\bFB[\w_]+\/|\bFacebook/i},
  {name: "twitter", regex: /\bTwitter/i},
  {name: "line", regex: /\bLine\//i},
  {name: "wechat", regex: /\bMicroMessenger\//i},
  {name: "threads", regex: /\bBarcelona/i},
  {name: "tiktok", regex: /musical_ly|Bytedance/i},
  {name: "snapchat", regex: /Snapchat/i},
  {name: "linkedin", regex: /LinkedInApp/i},
  {name: "gsa", regex: /GSA/i},
  {name: "whatsapp", regex: /\b(WAiOS|WA4A)\//i},
];

const GENERIC_WEBVIEW_REGEX =
  /(WebView)|((iPhone|iPod|iPad)(?!.*Safari\/))|(Android.*wv\))/i;

const isTelegram = (): boolean =>
  typeof window !== "undefined" &&
  !!(window as unknown as {TelegramWebviewProxy?: unknown})
    .TelegramWebviewProxy;

const detectInAppName = (ua: string): InAppName | null => {
  for (const {name, regex} of APP_REGEXES) {
    if (regex.test(ua)) return name;
  }
  if (isTelegram()) return "telegram";
  if (GENERIC_WEBVIEW_REGEX.test(ua)) return "webview";
  return null;
};

/**
 * Detects the runtime environment for GA4 reporting.
 *
 * Returns `"pwa"` when the app is launched from the home screen in standalone
 * display mode, `"inapp:<name>"` when running inside an in-app browser
 * (social apps, webviews), or `"browser"` as the default fallback.
 *
 * SSR-safe: returns `"browser"` when `window` is undefined.
 */
export const detectEnvironment = (): AppEnvironment => {
  if (typeof window === "undefined") return "browser";
  if (isPWA) return "pwa";
  const inApp = detectInAppName(window.navigator.userAgent || "");
  return inApp ? `inapp:${inApp}` : "browser";
};
